import { useState } from "react"
import { ArrowRight } from "@untitledui/icons"
import type { BusinessDetails } from "@/lib/api/onboarding-activation"
import {
  PrimaryButton,
  StageHeader,
  Field,
  AInput,
  ActionRow,
  ASelect,
  SelectTile,
  focusFirstInvalid,
} from "./ui"

// Option lists mirror step 3 of CCi's Zoho onboarding form verbatim.
const BMS_SYSTEMS = [
  "CCCOne",
  "Mitchell Repair Center",
  "ABWTracker",
  "BodyShop Connect",
  "IMex",
  "ProfitNet",
  "ROME",
  "SUMMIT",
  "NexSyis",
  "Collision Flo",
  "Other",
]
const ESTIMATING_SYSTEMS = ["CCC", "Mitchell", "Audatex", "Other"]

// Stage 2 — which management system the shop runs and which estimating
// system(s), multi-select. Routes CCi's data connection.
export function SystemsStep({
  draft,
  onBack,
  onNext,
}: {
  draft: BusinessDetails
  onBack: () => void
  onNext: (patch: BusinessDetails) => void
}) {
  const [bms, setBms] = useState(draft.bmsSystem ?? "")
  const [systems, setSystems] = useState<string[]>(draft.estimatingSystems ?? [])
  const [other, setOther] = useState(draft.estimatingOther ?? "")
  const [tried, setTried] = useState(false)

  const toggle = (s: string) =>
    setSystems((prev) => (prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]))

  const otherChecked = systems.includes("Other")
  const errors = {
    systems: systems.length === 0 && "Pick at least one — this is what CCi connects to",
    other: otherChecked && !other.trim() && "Name the other system",
  }
  const invalid = Object.values(errors).some(Boolean)

  const next = () => {
    if (invalid) {
      setTried(true)
      focusFirstInvalid()
      return
    }
    onNext({
      bmsSystem: bms || undefined,
      estimatingSystems: systems,
      estimatingOther: otherChecked ? other.trim() : undefined,
    })
  }

  return (
    <div>
      <StageHeader
        stage="Step 2 of 5"
        title="The systems you use"
        lede="Tick the estimating system you write estimates in. If you use more than one, tick them all."
      />

      <div className="space-y-4">
        <Field label="Estimating system" error={tried && errors.systems}>
          <div className="grid grid-cols-2 gap-2.5" role="group">
            {ESTIMATING_SYSTEMS.map((s) => (
              <SelectTile
                key={s}
                role="checkbox"
                selected={systems.includes(s)}
                onClick={() => toggle(s)}
                invalid={Boolean(tried && errors.systems)}
              >
                {s}
              </SelectTile>
            ))}
          </div>
        </Field>

        {otherChecked && (
          <Field label="Which other estimating system?" error={tried && errors.other}>
            <AInput
              autoFocus
              value={other}
              onChange={(e) => setOther(e.target.value)}
              placeholder="Name of the system"
              valid={Boolean(other.trim())}
              invalid={Boolean(tried && errors.other)}
            />
          </Field>
        )}

        <Field label="Management system (optional)" hint="Skip this if you're not sure">
          <ASelect
            value={bms}
            onChange={setBms}
            options={BMS_SYSTEMS}
            placeholder="Choose from the list…"
          />
        </Field>
      </div>

      <ActionRow back={onBack} note={tried && invalid ? "One thing to fix — it's marked above" : undefined}>
        <PrimaryButton onClick={next}>
          Continue
          <ArrowRight className="size-4" />
        </PrimaryButton>
      </ActionRow>
    </div>
  )
}
